import React, { Fragment } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Input,
  CardImg,
  CardTitle,
  Container,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import NumericPropertyInput from "./NumericPropertyInput";

/** A stored property value; the boolean select stores `true` / `false`. */
export type ProductPropertyValue = string | number | boolean;

/** One row of a product-properties record (GET /api/productproperties/:id). */
export type ProductPropertyRow = {
  _id?: string;
  key?: string;
  name: string;
  type?: string;
  value?: ProductPropertyValue;
};

/** The product-properties record the Update screen edits. */
export type ProductPropertiesRecord = {
  _id?: string;
  properties?: ProductPropertyRow[];
  product?: { title?: string; productImg?: string };
  subcategory?: { name?: string; category?: { name?: string } };
};

type UpdateProductPropertiesViewProps = {
  productProps: ProductPropertiesRecord | null;
  product?: ProductPropertiesRecord["product"];
  subcategory?: ProductPropertiesRecord["subcategory"];
  canEdit: boolean;
  onFieldChange: (name: string, value: ProductPropertyValue) => void;
  onUpdate: () => void;
  onBackToListHref: string;
};

const UpdateProductPropertiesView = ({
  productProps,
  product,
  subcategory,
  canEdit,

  onFieldChange,
  onUpdate,
  onBackToListHref,
}: UpdateProductPropertiesViewProps) => {
  return (
    <>
      {/* Notifications */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Update Product Properties" parentName="Products" />
      <Container className="mt--6" fluid>
        {productProps ? (
          <Row>
            <Col className="order-xl-1" xl="8">
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col xs="8">
                      <h3 className="mb-0">Update Properties</h3>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody>
                  <h6 className="heading-small text-muted mb-4">
                    Properties List
                  </h6>

                  {Array.isArray(productProps?.properties) &&
                    productProps.properties.length > 0 &&
                    productProps.properties.map((property) => {
                      const renderInput = () => {
                        const common = {
                          placeholder: property.name,
                          name: property.name,
                          // The default branch renders a text input, whose stored value is a string.
                          value: (property.value ?? "") as string | number,
                          onChange: (e) => onFieldChange(property.name, e.target.value),
                        };

                        switch (property.type) {
                          case "float":
                          case "number":
                          case "currency":
                          case "interest":
                            return (
                              <NumericPropertyInput
                                name={property.name}
                                value={(property.value ?? "") as string | number}
                                onChange={onFieldChange}
                              />
                            );
                          case "boolean":
                            return (
                              <Input
                                type="select"
                                name={property.name}
                                value={String(property.value ?? "")}
                                onChange={(e) =>
                                  onFieldChange(property.name, e.target.value === "true")
                                }
                              >
                                <option value="">Select..</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </Input>
                            );
                          default:
                            return <Input type="text" {...common} />;
                        }
                      };

                      return (
                        <Fragment key={property._id || property.key || property.name}>
                          <FormGroup className="row">
                            <Label
                              className="form-control-label"
                              htmlFor="example-text-input"
                              md="2"
                            >
                              {property.name}
                            </Label>
                            <Label
                              className="form-control-label"
                              htmlFor="example-text-input"
                              md="2"
                            >
                              {property.type}
                            </Label>
                            <Col md="7">
                              <InputGroup className={classnames("input-group-merge")}>
                                {renderInput()}
                              </InputGroup>
                            </Col>
                          </FormGroup>
                        </Fragment>
                      );
                    })}

                  {canEdit ? (
                    <Button color="primary" onClick={onUpdate}>
                      Update
                    </Button>
                  ) : null}

                  <Button to={onBackToListHref} tag={Link} className="btn btn-default ml-2">
                    Back
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {product && (
              <Col className="order-xl-2" xl="4">
                <Card>
                  <CardImg alt="..." src={product.productImg || ""} top />
                  <CardBody>
                    <CardTitle className="h2 mb-0">
                      {product.title}
                      <Row>
                        <small className="text-muted ml-3 mt-2">
                          {subcategory?.category?.name}
                        </small>
                      </Row>
                      <Row>
                        <small className="text-muted ml-3 mt-2">
                          {subcategory?.name}
                        </small>
                      </Row>
                    </CardTitle>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        ) : null}
      </Container>
    </>
  );
};

export default UpdateProductPropertiesView;
